import React, { useMemo, useState } from 'react';
import { Button, HStack, Spinner, Stack, useDisclosure, VStack, } from '@chakra-ui/react';
import { keepPreviousData } from '@tanstack/react-query';
import { useGetAllOfficers } from '../../../../services/api/generated/client';
import { unwrapModel } from '../../../../services/api/unwrapModel';
import { ErrorAwareAlertBox } from '../../../modules/ErrorAwareAlertBox/ErrorAwareAlertBox';
import UsersTable from '../../../modules/UsersTable/UsersTable';
import SearchBar from '../../../modules/SearchBar/SearchBar';
import { useTokens } from '../../../../context/TokenProvider';
import { localise } from '../../../../services/lang/lang';
import { buildPaginationFromResponse } from '../../../../services/pagination';
import usePages from '../../../../hooks/usePages';
import PermissionFilterWrapper from './PermissionFilterWrapper';
import { InviteTeamModal } from '../../../modules/InviteTeamModal/InviteTeamModal';
import useSorting from '../../../../hooks/useSorting';
import { PermissionGuardBanner } from '../../../modules/PermissionGuard/PermissionGuardBanner';
import { ActiveUsersTableMenuOptions } from './ActiveUsersTableMenuOptions';
import UsersTableRow from '../../../modules/UsersTable/UsersTableRow';
import { ActiveUsersTableContext } from './ActiveUsersTableContext';
import useCanUserViewFeature from '../../../../hooks/useCanUserViewFeature';
import useSearch from '../../../../hooks/useSearch';
const ActiveUsersWrapper = ({ canInvite }) => {
    const { incrementPage, decrementPage, page, setPage } = usePages();
    const { searchQuery, debouncedSetSearchQuery } = useSearch(() => {
        setPage(1);
    });
    const inviteModal = useDisclosure();
    const { canView: canPerformActions } = useCanUserViewFeature(['users.set-roles'], undefined, 'any');
    const providerValue = useMemo(() => ({ canPerformActions }), [canPerformActions]);
    const [roleIds, setRoleIds] = useState([]);
    const { sortFormatted, sortOptions, handleSort } = useSorting();
    const handlePermissionsApplied = (ids) => {
        setPage(1);
        setRoleIds(ids);
    };
    const { data: response, isError, error, isFetching, refetch, } = useGetAllOfficers({
        'filter[search_query]': searchQuery !== '' ? searchQuery : undefined,
        'filter[role_ids]': roleIds,
        should_paginate: 1,
        sort: sortFormatted,
        page,
    }, { query: { placeholderData: keepPreviousData } });
    const tokens = useTokens();
    if (isError || error) {
        return React.createElement(ErrorAwareAlertBox, { error: error });
    }
    const users = unwrapModel(response);
    if (!users && isFetching) {
        return React.createElement(Spinner, { size: "lg" });
    }
    const pagination = buildPaginationFromResponse(response, incrementPage, decrementPage);
    return (React.createElement(ActiveUsersTableContext.Provider, { value: providerValue },
        React.createElement(VStack, { "data-testid": "active_users_wrapper", align: "start", spacing: tokens.Spacing07 },
            React.createElement(PermissionGuardBanner, { permissions: [
                    'users.set-roles',
                    'service-provider.users-invite-create',
                ] }),
            React.createElement(Stack, { direction: { base: 'column', xl: 'row' }, alignItems: { base: 'stretch', xl: 'flex-end' }, spacing: { base: tokens.Spacing04, xl: tokens.Spacing06 }, width: "100%" },
                React.createElement(SearchBar, { placeholder: localise('SEARCH_NAME_EMAIL'), onChange: debouncedSetSearchQuery }),
                React.createElement(HStack, { justifyContent: { base: 'flex-start', xl: 'space-between' }, width: { base: 'auto', xl: '100%' }, mt: { base: tokens.Spacing07, xl: '0px' } },
                    React.createElement(PermissionFilterWrapper, { onApply: handlePermissionsApplied }),
                    React.createElement(Button, { "data-testid": "active_users_wrapper__invite_team_button", marginLeft: { base: tokens.Spacing05, xl: 'auto' }, variant: "secondary", isDisabled: !canInvite, onClick: inviteModal.onOpen }, localise('INVITE_TEAM')))),
            React.createElement(UsersTable, { currentSortOptions: sortOptions, isFetching: isFetching, users: users, pagination: pagination, onSort: handleSort }, users?.map((user) => (React.createElement(UsersTableRow, { key: user.id, user: user },
                React.createElement(ActiveUsersTableMenuOptions, { user: user, onChange: refetch }))))),
            React.createElement(InviteTeamModal, { isOpen: inviteModal.isOpen, onClose: inviteModal.onClose }))));
};
export default ActiveUsersWrapper;
